import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeBookings: 0,
    monthlyRevenue: 0,
    pendingPayments: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulated data - replace with API calls
    setStats({
      totalMembers: 247,
      activeBookings: 89,
      monthlyRevenue: 12450,
      pendingPayments: 12
    });
    setLoading(false);
  }, []);

  const quickActions = [
    { icon: '👤', label: 'Add Member', color: 'pink', path: '/admin/users' },
    { icon: '📅', label: 'Schedule Class', color: 'cyan', path: '/admin/calendar' },
    { icon: '💰', label: 'Record Payment', color: 'gold', path: '/admin/payments' },
    { icon: '✉️', label: 'Send Message', color: 'purple', path: '/admin/messages' },
  ];

  const recentActivity = [
    { user: 'Emma Wilson', action: 'Booked', detail: 'Advanced Tumbling - Monday', time: '2 min ago' },
    { user: 'James Chen', action: 'Paid', detail: '£85 - Monthly Membership', time: '15 min ago' },
    { user: 'Sarah Mitchell', action: 'Registered', detail: 'New member - Recreational', time: '1 hour ago' },
    { user: 'Coach Mike', action: 'Updated', detail: 'Class schedule - Tuesday', time: '2 hours ago' },
    { user: 'Lisa Brown', action: 'Cancelled', detail: 'Competition Training - Friday', time: '3 hours ago' },
  ];

  const upcomingClasses = [
    { time: '09:00', name: 'Beginner Rec', instructor: 'Coach Anna', spots: 5 },
    { time: '10:30', name: 'Intermediate', instructor: 'Coach Mike', spots: 3 },
    { time: '12:00', name: 'Advanced Tumbling', instructor: 'Coach Sarah', spots: 2 },
    { time: '14:00', name: 'Competitive Team', instructor: 'Coach James', spots: 0 },
    { time: '16:00', name: 'Youth Development', instructor: 'Coach Emma', spots: 8 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Welcome Back!</h1>
          <p className="text-gray-400 mt-1">Here's what's happening at Elite Gymnastics today.</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 rounded-xl glass text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Report
          </button>
          <button className="btn-vip !py-2 !px-6 !text-base">
            Quick Action
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card stat-card-pink rounded-2xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Members</p>
              <p className="text-4xl font-bold text-white mt-2">{stats.totalMembers}</p>
              <p className="text-pink-400 text-sm mt-2">↑ 12% this month</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-pink-500/20 flex items-center justify-center text-3xl">
              👥
            </div>
          </div>
        </div>

        <div className="stat-card stat-card-cyan rounded-2xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Bookings</p>
              <p className="text-4xl font-bold text-white mt-2">{stats.activeBookings}</p>
              <p className="text-cyan-400 text-sm mt-2">↑ 8% this week</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-3xl">
              📅
            </div>
          </div>
        </div>

        <div className="stat-card stat-card-gold rounded-2xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-400 text-sm">Monthly Revenue</p>
              <p className="text-4xl font-bold text-white mt-2">£{stats.monthlyRevenue.toLocaleString()}</p>
              <p className="text-amber-400 text-sm mt-2">↑ 15% vs last month</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center text-3xl">
              💰
            </div>
          </div>
        </div>

        <div className="stat-card rounded-2xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-400 text-sm">Pending Payments</p>
              <p className="text-4xl font-bold text-white mt-2">{stats.pendingPayments}</p>
              <p className="text-gray-400 text-sm mt-2">Requires attention</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center text-3xl">
              ⚠️
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className={`group p-4 rounded-xl glass hover:bg-white/10 transition-all duration-300 hover:scale-105 ${
                action.color === 'pink' ? 'hover:shadow-glow-pink' :
                action.color === 'gold' ? 'hover:shadow-glow-gold' : ''
              }`}
            >
              <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">{action.icon}</div>
              <div className="text-white font-medium text-sm">{action.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Classes */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Today's Classes</h2>
            <Link to="/admin/calendar" className="text-pink-400 text-sm hover:text-pink-300 transition-colors">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {upcomingClasses.map((cls, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="w-16 text-center">
                  <div className="text-amber-400 font-bold">{cls.time}</div>
                </div>
                <div className="flex-1">
                  <div className="text-white font-medium">{cls.name}</div>
                  <div className="text-gray-500 text-sm">{cls.instructor}</div>
                </div>
                <div className={`text-sm px-3 py-1 rounded-full ${
                  cls.spots === 0 
                    ? 'bg-red-500/20 text-red-400' 
                    : cls.spots < 5 
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  {cls.spots === 0 ? 'Full' : `${cls.spots} spots`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Recent Activity</h2>
            <Link to="/admin/reports" className="text-pink-400 text-sm hover:text-pink-300 transition-colors">
              View All →
            </Link>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-amber-400 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {activity.user.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">
                    <span className="font-semibold">{activity.user}</span>
                    {' '}
                    <span className="text-gray-400">{activity.action}</span>
                  </div>
                  <div className="text-gray-500 text-xs truncate">{activity.detail}</div>
                </div>
                <div className="text-gray-600 text-xs flex-shrink-0">{activity.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Membership Distribution Chart (Visual) */}
      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-6">Membership Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-500/20">
            <div className="text-4xl font-bold text-pink-400">89</div>
            <div className="text-gray-400 text-sm mt-1">Recreational</div>
          </div>
          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/20">
            <div className="text-4xl font-bold text-cyan-400">67</div>
            <div className="text-gray-400 text-sm mt-1">Development</div>
          </div>
          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/20">
            <div className="text-4xl font-bold text-amber-400">56</div>
            <div className="text-gray-400 text-sm mt-1">Competitive</div>
          </div>
          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 border border-purple-500/20">
            <div className="text-4xl font-bold text-purple-400">35</div>
            <div className="text-gray-400 text-sm mt-1">Birthday Parties</div>
          </div>
        </div>
      </div>
    </div>
  );
}